import '../../global/actions/initial';

import { memo, useEffect } from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { GlobalState } from '../../global/types';

import { DEBUG } from '../../config';
import { IS_TAURI } from '../../util/browser/globalEnvironment';
import { IS_MAC_OS, PLATFORM_ENV } from '../../util/browser/windowEnvironment';
import { DEBUG_PHONE_AUTH_MARKER } from '../../util/debugLoginMarkers';
import {
  hasPendingWebAuthToken,
  shouldPreferPhoneAuth,
  stripPreferredPhoneAuthParam,
} from '../../util/googleAuth';
import { callApi } from '../../api/gramjs/worker/connector';
import { getPendingPhoneLoginAction } from './debugLoginState';

import useCurrentOrPrev from '../../hooks/useCurrentOrPrev';
import useHistoryBack from '../../hooks/useHistoryBack';

import Loading from '../ui/Loading';
import Transition from '../ui/Transition';
import AuthCode from './AuthCode.async';
import AuthPassword from './AuthPassword.async';
import AuthPhoneNumber from './AuthPhoneNumber';
import AuthQrCode from './AuthQrCode';
import AuthRegister from './AuthRegister.async';

import './Auth.scss';

type StateProps = {
  authState: GlobalState['auth']['state'];
  connectionState?: GlobalState['connectionState'];
  hasWebAuthTokenFailed?: boolean;
};

type DebugPhoneLoginPayload = {
  phone: string;
  code: string;
  password?: string;
};

const SAAS_OWNER_HANDOFF_PENDING_DISMISSED_KEY = 'runai:saas-owner-handoff:pending-dismissed';

const Auth = ({
  authState,
  connectionState,
  hasWebAuthTokenFailed,
}: StateProps) => {
  const {
    returnToAuthPhoneNumber, goToAuthQrCode,
  } = getActions();

  const isMobile = PLATFORM_ENV === 'iOS' || PLATFORM_ENV === 'Android';
  const isConnectionReady = connectionState === 'connectionStateReady';
  const isConnectionBroken = connectionState === 'connectionStateBroken';

  const hasDismissedSaasHandoffPending = (() => {
    try {
      return window.sessionStorage.getItem(SAAS_OWNER_HANDOFF_PENDING_DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  })();

  const handleChangeAuthorizationMethod = () => {
    if (!isMobile) {
      goToAuthQrCode();
    } else {
      returnToAuthPhoneNumber();
    }
  };

  useHistoryBack({
    isActive: (!isMobile && authState === 'authorizationStateWaitPhoneNumber')
      || (isMobile && authState === 'authorizationStateWaitQrCode'),
    onBack: handleChangeAuthorizationMethod,
  });

  // For animation purposes
  const renderingAuthState = useCurrentOrPrev(
    authState !== 'authorizationStateReady' ? authState : undefined,
    true,
  );

  const isSaasOwnerAccessTokenLane = (() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      return searchParams.get('run_source') === 'saas'
        && Boolean(searchParams.get('ownerAccessToken')?.trim());
    } catch {
      return false;
    }
  })();

  const shouldRenderSaasHandoffPending = isSaasOwnerAccessTokenLane
    && !isConnectionBroken
    && !hasWebAuthTokenFailed
    && !hasDismissedSaasHandoffPending
    && (
      renderingAuthState === undefined
      || renderingAuthState === 'authorizationStateWaitPhoneNumber'
      || renderingAuthState === 'authorizationStateWaitQrCode'
      || renderingAuthState === 'authorizationStateWaitGoogleAuth'
    );

  useEffect(() => {
    if (!isSaasOwnerAccessTokenLane) {
      try {
        window.sessionStorage.removeItem(SAAS_OWNER_HANDOFF_PENDING_DISMISSED_KEY);
      } catch {
        // Ignore storage failures; this is only a UX latch.
      }
      return;
    }

    if (
      hasWebAuthTokenFailed
      && (
        typeof authState !== 'string'
        || authState === 'authorizationStateWaitPhoneNumber'
        || authState === 'authorizationStateWaitQrCode'
      )
    ) {
      try {
        window.sessionStorage.setItem(SAAS_OWNER_HANDOFF_PENDING_DISMISSED_KEY, '1');
      } catch {
        // Ignore storage failures; fallback stays on current auth state.
      }
      return;
    }

    if (authState === 'authorizationStateReady') {
      try {
        window.sessionStorage.removeItem(SAAS_OWNER_HANDOFF_PENDING_DISMISSED_KEY);
      } catch {
        // Ignore storage failures after success.
      }
    }
  }, [authState, hasWebAuthTokenFailed, isSaasOwnerAccessTokenLane]);

  function getScreen() {
    const shouldRenderPhoneByDefault = isMobile || shouldPreferPhoneAuth(window.location.search);

    if (shouldRenderSaasHandoffPending) {
      return (
        <div id="auth-saas-handoff-pending" className="auth-form auth-saas-handoff-pending">
          <Loading />
          <h1>Entering your AI team...</h1>
          <p className="note">We are finishing your secure sign-in to Sage.</p>
        </div>
      );
    }

    switch (renderingAuthState) {
      case 'authorizationStateWaitCode':
        return <AuthCode />;
      case 'authorizationStateWaitPassword':
        return <AuthPassword />;
      case 'authorizationStateWaitRegistration':
        return <AuthRegister />;
      case 'authorizationStateWaitPhoneNumber':
        return <AuthPhoneNumber />;
      case 'authorizationStateWaitGoogleAuth':
        return <AuthPhoneNumber />;
      case 'authorizationStateWaitQrCode':
        return <AuthQrCode />;
      default:
        return shouldRenderPhoneByDefault ? <AuthPhoneNumber /> : <AuthQrCode />;
    }
  }

  function getActiveKey() {
    const shouldRenderPhoneByDefault = isMobile || shouldPreferPhoneAuth(window.location.search);

    if (shouldRenderSaasHandoffPending) {
      return 6;
    }

    switch (renderingAuthState) {
      case 'authorizationStateWaitCode':
        return 0;
      case 'authorizationStateWaitPassword':
        return 1;
      case 'authorizationStateWaitRegistration':
        return 2;
      case 'authorizationStateWaitPhoneNumber':
        return 3;
      case 'authorizationStateWaitGoogleAuth':
        return 5;
      case 'authorizationStateWaitQrCode':
        return 4;
      default:
        return shouldRenderPhoneByDefault ? 3 : 4;
    }
  }

  const handleDebugLogin = () => {
    (window as any).__pendingPhoneLogin = {
      phone: '+16502859925',
      code: '12345',
      password: '12345',
    };
    getActions().setAuthRememberMe({ value: true });
    returnToAuthPhoneNumber();
  };

  // Expose for CDP-based automated testing (CU agent)
  (window as any).__debugLogin = handleDebugLogin;

  // Event-driven auto-login: reacts to authState changes instead of setTimeout chains.
  // Stored on window so it persists across re-renders of this component.
  const readPendingPhoneLoginFallback = (): DebugPhoneLoginPayload | undefined => {
    try {
      if ((window as any).__pendingPhoneLogin) {
        return (window as any).__pendingPhoneLogin as
          DebugPhoneLoginPayload;
      }

      if (window.sessionStorage.getItem(DEBUG_PHONE_AUTH_MARKER) !== '1') {
        return undefined;
      }

      const raw = window.sessionStorage.getItem('runai:debug-login:phone-payload');
      if (!raw) {
        return undefined;
      }

      const parsed = JSON.parse(raw) as { code?: string; phone?: string; password?: string };
      if (!parsed?.code || !parsed?.phone) {
        return undefined;
      }

      return {
        code: parsed.code,
        phone: parsed.phone,
        password: parsed.password,
      };
    } catch {
      return undefined;
    }
  };

  const pendingLogin = readPendingPhoneLoginFallback();
  const pendingPhoneLoginAction = getPendingPhoneLoginAction(
    Boolean(pendingLogin),
    authState,
    isConnectionReady,
  );
  const readPendingAutoCodeFallback = (): DebugPhoneLoginPayload | undefined => {
    try {
      if ((window as any).__pendingAutoCode) {
        return (window as any).__pendingAutoCode as DebugPhoneLoginPayload;
      }

      if (window.sessionStorage.getItem(DEBUG_PHONE_AUTH_MARKER) !== '1') {
        return undefined;
      }

      const raw = window.sessionStorage.getItem('runai:debug-login:phone-payload');
      if (!raw) {
        return undefined;
      }

      const parsed = JSON.parse(raw) as { code?: string; phone?: string; password?: string };
      if (!parsed?.code || !parsed?.phone) {
        return undefined;
      }

      return {
        code: parsed.code,
        phone: parsed.phone,
        password: parsed.password,
      };
    } catch {
      return undefined;
    }
  };

  useEffect(() => {
    if (pendingPhoneLoginAction === 'returnToPhoneNumber') {
      returnToAuthPhoneNumber();
    }
  }, [pendingPhoneLoginAction, returnToAuthPhoneNumber]);

  useEffect(() => {
    if (!shouldPreferPhoneAuth(window.location.search)) return;
    if (hasPendingWebAuthToken(window.location.hash)) return;
    if (typeof authState === 'string' && authState !== 'authorizationStateWaitQrCode') return;
    returnToAuthPhoneNumber();
    window.history.replaceState({}, '', stripPreferredPhoneAuthParam(window.location.href));
  }, [authState, connectionState, returnToAuthPhoneNumber]);

  if (pendingLogin && authState) {
    const phone = pendingLogin.phone.replace(/[^\d+]/g, '');
    const { code, password } = pendingLogin;

    if (pendingPhoneLoginAction === 'providePhone') {
      const submittedPhone = window.sessionStorage.getItem('runai:debug-login:submitted-phone');
      if (submittedPhone !== phone) {
        // GramJS is ready for phone number — provide it immediately
        window.sessionStorage.setItem('runai:debug-login:submitted-phone', phone);
        // eslint-disable-next-line no-console
        console.log('[debug_login] authState=WaitPhoneNumber → providing phone');
        delete (window as any).__pendingPhoneLogin;
        // Small delay to ensure the auth controller promise is set up
        setTimeout(() => {
          callApi('provideAuthPhoneNumber', phone.replace(/[^\d]/g, ''));
          // Re-store for next state transition
          (window as any).__pendingAutoCode = { code, password, phone };
        }, 500);
      }
    } else if (authState === 'authorizationStateWaitCode') {
      const autoCode = readPendingAutoCodeFallback();
      if (autoCode) {
        delete (window as any).__pendingAutoCode;
        // eslint-disable-next-line no-console
        console.log('[debug_login] authState=WaitCode → providing code');
        setTimeout(() => {
          callApi('provideAuthCode', autoCode.code);
          (window as any).__pendingAutoRegistration = { phone: autoCode.phone };
          if (autoCode.password) {
            (window as any).__pendingAutoPassword = autoCode.password;
          }
        }, 500);
      }
    } else if (
      authState === 'authorizationStateWaitPassword'
      && (window as any).__pendingAutoPassword
      && isConnectionReady
    ) {
      const pw = (window as any).__pendingAutoPassword;
      delete (window as any).__pendingAutoPassword;
      // eslint-disable-next-line no-console
      console.log('[debug_login] authState=WaitPassword → providing password');
      setTimeout(
        () => getActions().checkPassword({ currentPassword: pw, onSuccess: () => {} }),
        500,
      );
    } else if (authState === 'authorizationStateWaitRegistration') {
      // New user — auto-register with phone tail as name
      // eslint-disable-next-line no-console
      console.log('[debug_login] authState=WaitRegistration → auto-registering');
      setTimeout(() => {
        callApi('provideAuthRegistration', {
          firstName: phone.replace(/\D/g, '').slice(-7), lastName: '',
        });
      }, 500);
    }
  }

  // Also handle the WaitCode/WaitPassword states when pendingLogin was already consumed
  if (!pendingLogin && authState === 'authorizationStateWaitCode') {
    const autoCode = readPendingAutoCodeFallback();
    if (autoCode) {
      delete (window as any).__pendingAutoCode;
      // eslint-disable-next-line no-console
      console.log('[debug_login] authState=WaitCode → providing code (deferred)');
      setTimeout(() => {
        callApi('provideAuthCode', autoCode.code);
        (window as any).__pendingAutoRegistration = { phone: autoCode.phone };
        if (autoCode.password) {
          (window as any).__pendingAutoPassword = autoCode.password;
        }
      }, 500);
    }
  }
  if (
    !pendingLogin
    && authState === 'authorizationStateWaitPassword'
    && (window as any).__pendingAutoPassword
    && isConnectionReady
  ) {
    const pw = (window as any).__pendingAutoPassword;
    delete (window as any).__pendingAutoPassword;
    // eslint-disable-next-line no-console
    console.log('[debug_login] authState=WaitPassword → providing password (deferred)');
    setTimeout(
      () => getActions().checkPassword({ currentPassword: pw, onSuccess: () => {} }),
      500,
    );
  }
  if (authState === 'authorizationStateWaitRegistration' && (window as any).__pendingAutoRegistration) {
    const registration = (window as any).__pendingAutoRegistration;
    delete (window as any).__pendingAutoRegistration;
    // eslint-disable-next-line no-console
    console.log('[debug_login] authState=WaitRegistration → auto-registering (deferred)');
    setTimeout(() => {
      callApi('provideAuthRegistration', {
        firstName: String(registration.phone || '').replace(/\D/g, '').slice(-7),
        lastName: '',
      });
    }, 500);
  }

  // Legacy: expose __debugLoginAs for CDP-based testing (non-event-driven fallback)
  (window as any).__debugLoginAs = (phone: string, code: string, password?: string) => {
    (window as any).__pendingPhoneLogin = { phone, code, password };
    // Trigger re-render to pick up the pending login
    returnToAuthPhoneNumber();
  };

  return (
    <Transition
      activeKey={getActiveKey()}
      name="fade"
      className="Auth"
      data-tauri-drag-region={IS_TAURI && IS_MAC_OS ? true : undefined}
    >
      {getScreen()}
      {DEBUG && (
        <div style="position:fixed;bottom:10px;right:10px;z-index:9999">
          <button
            id="debug-login"
            type="button"
            onClick={handleDebugLogin}
            style={[
              'padding:8px 16px',
              'background:#e53935',
              'color:white',
              'border:none',
              'border-radius:4px',
              'cursor:pointer',
              'font-size:12px',
            ].join(';')}
          >
            Debug Login
          </button>
        </div>
      )}
    </Transition>
  );
};

export default memo(withGlobal(
  (global): Complete<StateProps> => {
    return {
      authState: global.auth.state,
      connectionState: global.connectionState,
      hasWebAuthTokenFailed: global.auth.hasWebAuthTokenFailed,
    };
  },
)(Auth));
